import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import './pmg-media-filter.js';

const PMS_STORAGE_KEY = 'seacase_selected_model';
const PMS_CHANGE_EVENT = 'seacase:model-changed';
const pmgMediaFilter = window.PmgMediaFilter;

function isThemeEditorPreview() {
  return Boolean(window.Shopify?.designMode || document.documentElement.classList.contains('shopify-design-mode'));
}

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];
  #explicitGalleryVariant = null;
  #currentGlobalModel = null;
  #galleryResetToken = 0;
  #galleryRefreshObserver = null;
  #galleryRefreshTimer = 0;
  #cardDebugEnabled = false;
  #lastCardGallerySelection = null;
  #lastCardGalleryResult = null;

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    if (isThemeEditorPreview()) {
      event.preventDefault();
      return;
    }

    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) {
      if (isThemeEditorPreview()) return;
      throw new Error('Product card link not found');
    }
    this.#handleQuickAdd();
    this.#setupCardGalleryDebug();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);
    document.addEventListener(PMS_CHANGE_EVENT, this.#handleGlobalModelChanged);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleCollectionFilterUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });

    requestAnimationFrame(() => this.#applyStoredGlobalModelToGallery());
    this.#scheduleCardGalleryFirstReset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    document.removeEventListener(PMS_CHANGE_EVENT, this.#handleGlobalModelChanged);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleCollectionFilterUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#galleryRefreshObserver?.disconnect();
    this.#galleryRefreshObserver = null;
    if (this.#galleryRefreshTimer) window.clearTimeout(this.#galleryRefreshTimer);
    this.#galleryRefreshTimer = 0;
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  #handleGlobalModelChanged = (event) => {
    if (this.closest('design-collection-products')) return;
    this.#currentGlobalModel = event.detail || {};
    this.#filterCardGalleryByModel(this.#currentGlobalModel);
  };

  #applyStoredGlobalModelToGallery() {
    if (this.closest('design-collection-products')) return;

    try {
      const stored = JSON.parse(sessionStorage.getItem(PMS_STORAGE_KEY) || 'null');
      if (stored) {
        this.#currentGlobalModel = stored;
        this.#filterCardGalleryByModel(stored);
      }
    } catch (e) {
      /* sessionStorage unavailable - keep the default product-card gallery. */
    }
  }

  #handleCollectionFilterUpdate = () => {
    this.#scheduleCardGalleryFirstReset(true);
  };

  #handlePageShow = () => {
    this.#scheduleCardGalleryFirstReset();
  };

  #scheduleCardGalleryFirstReset(observeRefresh = false) {
    const reset = () => {
      if (!this.isConnected) return;
      const slideshow = this.refs.slideshow;
      const firstVisibleSlide = Array.from(slideshow?.refs?.slides || []).find((slide) => !slide.hidden);
      if (slideshow && firstVisibleSlide) {
        this.#resetCardGalleryToFirst(slideshow, firstVisibleSlide);
      }
    };

    reset();
    requestAnimationFrame(() => requestAnimationFrame(reset));

    if (!observeRefresh || !('MutationObserver' in window)) return;

    this.#galleryRefreshObserver?.disconnect();
    this.#galleryRefreshObserver = new MutationObserver(() => {
      requestAnimationFrame(reset);
    });
    this.#galleryRefreshObserver.observe(this.refs.cardGallery || this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'initial-slide', 'slide-id'],
    });

    if (this.#galleryRefreshTimer) window.clearTimeout(this.#galleryRefreshTimer);
    this.#galleryRefreshTimer = window.setTimeout(() => {
      this.#galleryRefreshObserver?.disconnect();
      this.#galleryRefreshObserver = null;
      this.#galleryRefreshTimer = 0;
      reset();
    }, 2500);
  }

  #getGlobalModelAliases(model) {
    return [model?.name, model?.handle].filter(Boolean);
  }

  #buildCardGallerySelection(slideshow, options, aliases = []) {
    const optionValues = Array.isArray(options) ? options.filter(Boolean) : [];
    const optionCount = Number(slideshow?.dataset?.pmgOptionCount);
    const hasOptionCount = Number.isInteger(optionCount) && optionCount > 0;

    return {
      options: hasOptionCount ? optionValues.slice(0, optionCount) : optionValues,
      aliases: [
        ...(hasOptionCount ? optionValues.slice(optionCount) : []),
        ...(Array.isArray(aliases) ? aliases : []),
      ].filter(Boolean),
    };
  }

  #setupCardGalleryDebug() {
    const panel = this.refs.cardGallery?.querySelector('[data-pmg-card-debug]');
    if (!panel) return;

    this.cardDebugPanel = panel;
    this.cardDebugAlt = panel.querySelector('[data-pmg-card-debug-alt]');
    this.cardDebugSelection = panel.querySelector('[data-pmg-card-debug-selection]');
    this.cardDebugResult = panel.querySelector('[data-pmg-card-debug-result]');
    this.cardDebugIndex = panel.querySelector('[data-pmg-card-debug-index]');
    this.#cardDebugEnabled =
      panel.dataset.pmgDebugEnabled === 'true' ||
      Boolean(window.Shopify?.designMode) ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === 'localhost' ||
      new URLSearchParams(window.location.search).get('pmg_debug') === '1';
    panel.hidden = !this.#cardDebugEnabled;

    if (this.#cardDebugEnabled) {
      requestAnimationFrame(() => this.#updateCardGalleryDebug());
    }
  }

  #updateCardGalleryDebug() {
    if (!this.#cardDebugEnabled || !this.cardDebugPanel) return;

    const slideshow = this.refs.slideshow;
    const visibleSlides = Array.from(slideshow?.slides || []);
    if (!slideshow || !visibleSlides.length) return;

    const currentSlide = visibleSlides[slideshow.current] || visibleSlides[0];
    const currentIndex = Math.max(visibleSlides.indexOf(currentSlide), 0);
    const rawAlt = currentSlide.dataset.pmgTags || currentSlide.querySelector('img')?.getAttribute('alt') || '';
    const selection = this.#lastCardGallerySelection;
    const result = this.#lastCardGalleryResult;
    const entry = pmgMediaFilter.preparePmgMedia([
      {
        index: 0,
        mediaId: currentSlide.getAttribute('slide-id'),
        rawTags: rawAlt,
      },
    ])[0];
    const inspection = pmgMediaFilter.inspectPmgEntry(entry, selection);
    let executionText = '等待产品卡片 PMG 执行…';

    if (result?.reason === 'all') {
      executionText = '执行成功：商品没有 PMG 标签，显示全部图片';
    } else if (result?.reason === 'matched' && inspection.matchedKeys.length) {
      executionText = `匹配成功：${inspection.matchedKeys.map((key) => `[pmg:${key}]`).join('、')}`;
    } else if (result?.reason === 'default' && inspection.default) {
      executionText = '回退成功：[pmg:default]';
    } else if (
      result?.reason === 'featured' &&
      entry.mediaId === String(selection?.featuredMediaId ?? '')
    ) {
      executionText = `回退成功：当前变体关联媒体 ${entry.mediaId}`;
    } else if (result && inspection.common) {
      executionText = entry.hasDirective
        ? '执行成功：显式公共图片 [pmg:common]'
        : '执行成功：无 PMG 标签，按公共图片展示';
    } else if (result?.reason === 'fallback') {
      executionText = '安全兜底：显示第一张可用图片';
    }

    if (inspection.issues.length) {
      executionText += `；标签警告：${inspection.issues.map((issue) => issue.code).join(', ')}`;
    }

    const selectedOptions = selection?.options?.length ? selection.options : selection?.aliases || [];
    if (this.cardDebugAlt) this.cardDebugAlt.textContent = rawAlt || '(空 ALT)';
    if (this.cardDebugSelection) {
      this.cardDebugSelection.textContent = selectedOptions.length
        ? selectedOptions.join(' | ')
        : '(尚未执行筛选)';
    }
    if (this.cardDebugResult) this.cardDebugResult.textContent = executionText;
    if (this.cardDebugIndex) {
      this.cardDebugIndex.textContent = `${currentIndex + 1} / ${visibleSlides.length}`;
    }
  }

  #filterCardGalleryByModel(model) {
    const modelAliases = this.#getGlobalModelAliases(model);
    if (!this.#explicitGalleryVariant) {
      this.#filterCardGallery({ options: [], aliases: modelAliases });
      return;
    }

    this.#filterCardGallery(
      {
        options: this.#explicitGalleryVariant.options,
        aliases: modelAliases,
      },
      this.#explicitGalleryVariant.media
    );
  }

  /**
   * Filters and sorts card media using the same PMG alt-tag rules as the product gallery.
   * Matching media is shown with common media; default media is used only when nothing matches.
   * If no default media exists, the selected Shopify variant media remains the final explicit fallback.
   * @param {{ options?: string[], media?: string | number | null } | null} variant
   * @returns {boolean} Whether the gallery was ready to be filtered.
   */
  filterGalleryByVariant(variant) {
    this.#explicitGalleryVariant = {
      options: Array.isArray(variant?.options) ? [...variant.options] : [],
      media: variant?.media ?? null,
    };

    return this.#filterCardGallery(
      {
        options: this.#explicitGalleryVariant.options,
        aliases: this.#getGlobalModelAliases(this.#currentGlobalModel),
      },
      this.#explicitGalleryVariant.media
    );
  }

  #filterCardGallery(selection, featuredMediaId = null) {
    const { slideshow } = this.refs;
    const slides = Array.from(slideshow?.refs?.slides || []);

    if (!slideshow || !slides.length) return false;

    const cardEntries = slides.map((slide, index) => {
      if (!slide.dataset.pmgOriginalIndex) slide.dataset.pmgOriginalIndex = String(index);
      const originalIndex = Number(slide.dataset.pmgOriginalIndex);
      return {
        slide,
        index: Number.isFinite(originalIndex) ? originalIndex : index,
        mediaId: slide.getAttribute('slide-id'),
        rawTags: slide.dataset.pmgTags || slide.querySelector('img')?.getAttribute('alt') || '',
      };
    });
    const mediaEntries = pmgMediaFilter.preparePmgMedia(cardEntries);
    const resolvedSelection = this.#buildCardGallerySelection(
      slideshow,
      selection?.options,
      selection?.aliases
    );
    const result = pmgMediaFilter.resolvePmgMedia(mediaEntries, {
      ...resolvedSelection,
      featuredMediaId,
    });
    this.#lastCardGallerySelection = {
      ...resolvedSelection,
      featuredMediaId,
    };
    this.#lastCardGalleryResult = result;

    // Cards without PMG directives keep their original media set, but filter refreshes
    // still return the carousel to its first image.
    if (result.reason === 'all') {
      this.#resetCardGalleryToFirst(slideshow, cardEntries[0]?.slide);
      return true;
    }

    const entriesByIndex = new Map(cardEntries.map((entry) => [entry.index, entry]));
    const visibleEntries = result.indexes
      .map((index) => entriesByIndex.get(index))
      .filter(Boolean);
    if (!visibleEntries.length && cardEntries.length) visibleEntries.push(cardEntries[0]);

    const visibleSet = new Set(visibleEntries.map((entry) => entry.slide));
    const hiddenEntries = cardEntries.filter((entry) => !visibleSet.has(entry.slide));

    this.#applyCardGalleryOrder(slideshow, visibleEntries, hiddenEntries);
    return true;
  }

  #applyCardGalleryOrder(slideshow, visibleEntries, hiddenEntries) {
    const orderedEntries = [...visibleEntries, ...hiddenEntries];
    const scroller = slideshow.refs?.scroller;
    const visibleSlides = new Set(visibleEntries.map((entry) => entry.slide));

    orderedEntries.forEach((entry, order) => {
      const orderValue = String(order);
      if (entry.slide.style.order !== orderValue) entry.slide.style.order = orderValue;
      entry.slide.hidden = !visibleSlides.has(entry.slide);
      entry.slide.setAttribute('aria-hidden', entry.slide.hidden ? 'true' : 'false');
    });

    const orderedSlides = orderedEntries.map((entry) => entry.slide);
    if (scroller) {
      const orderedSlideSet = new Set(orderedSlides);
      const currentSlides = Array.from(scroller.children).filter((child) => orderedSlideSet.has(child));
      const orderChanged =
        currentSlides.length !== orderedSlides.length ||
        orderedSlides.some((slide, index) => currentSlides[index] !== slide);

      if (orderChanged) {
        orderedSlides.forEach((slide) => {
          if (slide.parentElement === scroller) scroller.appendChild(slide);
        });
      }
    }

    try {
      const currentRefs = Array.from(slideshow.refs.slides || []);
      const refsChanged =
        currentRefs.length !== orderedSlides.length ||
        orderedSlides.some((slide, index) => currentRefs[index] !== slide);
      if (refsChanged) slideshow.refs.slides = orderedSlides;
    } catch (e) {
      /* Some slideshow refs may be readonly; CSS order + hidden state still applies. */
    }

    this.#resetCardGalleryToFirst(slideshow, visibleEntries[0]?.slide);
  }

  #resetCardGalleryToFirst(slideshow, firstVisibleSlide) {
    if (!slideshow || !firstVisibleSlide) return;

    this.resetVariant.cancel();
    this.#previousSlideIndex = null;
    const resetToken = ++this.#galleryResetToken;

    const reset = () => {
      if (resetToken !== this.#galleryResetToken || !this.isConnected || !firstVisibleSlide.isConnected) return;

      const firstVisibleId = firstVisibleSlide.getAttribute('slide-id');
      if (firstVisibleId) {
        void slideshow.select({ id: firstVisibleId }, undefined, { animate: false });
      }

      const scroller = slideshow.refs?.scroller;
      if (scroller) {
        scroller.scrollLeft = 0;
        scroller.scrollTop = 0;
      }
      slideshow.current = 0;
      this.#updateCardGalleryDebug();
    };

    reset();
    requestAnimationFrame(reset);
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
    requestAnimationFrame(() => this.#updateCardGalleryDebug());
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    if (isThemeEditorPreview()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
